import React from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

/**
 * EventImageModal - Full-screen image viewer with swipe/keyboard navigation
 * @param {boolean} open - Whether modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {string} imageSrc - Current image source URL
 * @param {string} capturedImageUrl - Captured image URL (for AI matches)
 * @param {string} personImage - Person reference image URL
 * @param {Function} onToggleImage - Callback to toggle between images
 */
export default function EventImageModal({
  open,
  onClose,
  imageSrc,
  capturedImageUrl,
  personImage,
  onToggleImage
}) {
  const [touchStart, setTouchStart] = React.useState(0);
  const [touchEnd, setTouchEnd] = React.useState(0);

  const minSwipeDistance = 50;

  const handleTouchStart = (e) => {
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe || isRightSwipe) {
      onToggleImage();
    }
  };

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        onToggleImage();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, onToggleImage, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.9)'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100vw',
          height: '100vh',
          outline: 'none'
        }}
        onClick={onClose}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            zIndex: 2,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.3)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Image indicator */}
        {capturedImageUrl && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              zIndex: 2
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: imageSrc === capturedImageUrl ? 'white' : 'rgba(255, 255, 255, 0.4)',
                transition: 'background-color 0.3s'
              }}
            />
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: imageSrc === personImage ? 'white' : 'rgba(255, 255, 255, 0.4)',
                transition: 'background-color 0.3s'
              }}
            />
          </Box>
        )}

        <Box
          component="img"
          src={imageSrc}
          alt="Enlarged view"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </Box>
    </Modal>
  );
}
